import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Paperclip, X } from "lucide-react";
import { INTERESTS } from "@/lib/data";
import { Reveal } from "@/components/primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-line bg-abyss/80 px-4 py-3 text-sm text-mist placeholder:text-mute/50 transition-colors duration-300 focus:border-electric/60 focus:outline-none";

export default function EnquiryForm({ defaultInterest = "General Enquiry", source = "website", heading = "Submit an Enquiry", lead, testid = "enquiry", onSuccess }) {
  const [form, setForm] = useState({ name: "", organisation: "", email: "", phone: "", interest: defaultInterest, message: "" });
  const [status, setStatus] = useState("idle");
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const ALLOWED = ["pdf", "doc", "docx", "xls", "xlsx", "png", "jpg", "jpeg"];
  const MAX_BYTES = 10 * 1024 * 1024;

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.includes(".") ? f.name.split(".").pop().toLowerCase() : "";
    if (!ALLOWED.includes(ext)) {
      setFile(null);
      setFileError("Unsupported file type. Please attach a PDF, DOC, DOCX, XLS, XLSX, PNG or JPG file.");
      return;
    }
    if (f.size > MAX_BYTES) {
      setFile(null);
      setFileError("File is too large. Please attach a file up to 10 MB.");
      return;
    }
    setFileError("");
    setFile(f);
  };

  const removeFile = () => {
    setFile(null);
    setFileError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const fd = new FormData();
      Object.entries({ ...form, source }).forEach(([k, v]) => fd.append(k, v ?? ""));
      if (file) fd.append("attachment", file);
      await axios.post(`${API}/enquiries`, fd);
      setStatus("success");
      toast.success("Enquiry submitted. Our team will reach out shortly.");
      onSuccess?.();
      setForm({ name: "", organisation: "", email: "", phone: "", interest: defaultInterest, message: "" });
      removeFile();
    } catch (err) {
      setStatus("idle");
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit your enquiry. Please try again.");
    }
  };

  return (
    <Reveal>
      <form onSubmit={submit} data-testid={`${testid}-form`} className="rounded-2xl border border-line bg-panel/70 p-8 sm:p-10">
        <h3 className="font-display text-2xl font-bold tracking-tight text-mist">{heading}</h3>
        {lead && <p className="mt-3 text-sm leading-relaxed text-mute">{lead}</p>}
        <div className="mt-8 grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor={`${testid}-name`} className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-mute">Name *</label>
            <input id={`${testid}-name`} data-testid={`${testid}-name-input`} required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" autoComplete="name" />
          </div>
          <div>
            <label htmlFor={`${testid}-org`} className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-mute">Organisation</label>
            <input id={`${testid}-org`} data-testid={`${testid}-org-input`} value={form.organisation} onChange={set("organisation")} className={inputCls} placeholder="Company / institution" autoComplete="organization" />
          </div>
          <div>
            <label htmlFor={`${testid}-email`} className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-mute">Email *</label>
            <input id={`${testid}-email`} data-testid={`${testid}-email-input`} type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" autoComplete="email" />
          </div>
          <div>
            <label htmlFor={`${testid}-phone`} className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-mute">Phone</label>
            <input id={`${testid}-phone`} data-testid={`${testid}-phone-input`} type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+91" autoComplete="tel" />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor={`${testid}-interest`} className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-mute">Interest *</label>
            <select id={`${testid}-interest`} data-testid={`${testid}-interest-select`} required value={form.interest} onChange={set("interest")} className={inputCls}>
              {INTERESTS.map((i) => (
                <option key={i} value={i} className="bg-abyss">{i}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label htmlFor={`${testid}-message`} className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-mute">Message *</label>
            <textarea id={`${testid}-message`} data-testid={`${testid}-message-input`} required rows={5} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Tell us what you'd like to build together" />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-mute">Attachment (Optional)</label>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              onChange={onFile}
              className="hidden"
              data-testid={`${testid}-attachment-input`}
              tabIndex={-1}
              aria-hidden="true"
            />
            {file ? (
              <div className="flex items-center justify-between gap-3 rounded-xl border border-line bg-abyss/80 px-4 py-3">
                <div className="flex min-w-0 items-center gap-3">
                  <Paperclip className="h-4 w-4 shrink-0 text-electric" />
                  <span className="truncate text-sm font-medium text-mist" data-testid={`${testid}-attachment-name`}>{file.name}</span>
                  <span className="shrink-0 font-mono text-[10px] text-mute">{(file.size / (1024 * 1024)).toFixed(2)} MB</span>
                </div>
                <button
                  type="button"
                  onClick={removeFile}
                  data-testid={`${testid}-attachment-remove-button`}
                  aria-label="Remove attachment"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-line text-mute transition-colors duration-300 hover:border-electric/50 hover:text-electric"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid={`${testid}-attachment-browse-button`}
                  className="flex w-full items-center gap-3 rounded-xl border border-dashed border-line bg-abyss/60 px-4 py-3 text-left transition-colors duration-300 hover:border-electric/50"
                >
                  <Paperclip className="h-4 w-4 shrink-0 text-mute" />
                  <span className="text-sm font-medium text-mist">Attach a file</span>
                  <span className="ml-auto hidden text-xs text-mute/70 sm:block">PDF, DOC, DOCX, XLS, XLSX, PNG or JPG — Max 10 MB</span>
                </button>
                <p className="mt-1.5 text-[11px] text-mute/70 sm:hidden">PDF, DOC, DOCX, XLS, XLSX, PNG or JPG — Max 10 MB</p>
              </>
            )}
            {fileError && (
              <p data-testid={`${testid}-attachment-error`} className="mt-2 text-xs text-red-500">{fileError}</p>
            )}
          </div>
        </div>
        <button
          type="submit"
          data-testid={`${testid}-submit-button`}
          disabled={status === "loading"}
          className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-electric px-6 py-3.5 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-teal disabled:opacity-60 sm:w-auto"
        >
          {status === "loading" && <Loader2 className="h-4 w-4 animate-spin" />}
          {status === "loading" ? "Submitting…" : "Submit Enquiry"}
        </button>
        <p className="mt-4 text-xs leading-relaxed text-mute/70">
          Your details are used only to respond to this enquiry. We never ask for passwords, OTPs or card details.
        </p>
      </form>
    </Reveal>
  );
}
